import { ArrowLeft, ArrowRight, Camera, Check, CheckCircle2, Laptop, RefreshCw, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { VehicleDetail } from "../../shared/types";
import type { PhotoCategory } from "../../shared/domain";
import { apiFetch, apiMutation } from "../api";
import { prepareVehiclePhotoFiles, uploadVehiclePhoto } from "../photo-library";
import { useAuth } from "../auth";
import { Modal, useToast } from "../components";
import { offlineQueueOwner, readVehicleIntakeDraft, writeVehicleIntakeDraft, type OfflineQueueScope } from "../offline-queue";
import { emptyIntakeDraft, IntakeDraftSession, intakeDraftSchema, intakeFieldErrors, intakeHasIdentity, intakeMissingData, type IntakeDraft, type IntakeFields } from "../vehicle-intake-draft";
import { removeVehiclePhotoFile, vehiclePhotoSceneFlowUrl } from "../vehicle-photo-intake";
import "../vehicle-intake.css";

interface Props { open: boolean; onClose(): void; onCreated?(): void }

export function VehicleIntakeModal({ open, ...props }: Props) {
  const { user } = useAuth();
  if (!open || !user) return null;
  return <Intake key={`${user.tenant.id}:${user.id}`} {...props} scope={{ tenantId: user.tenant.id, userId: user.id }} />;
}

function Intake({ onClose, onCreated, scope: scopeInput }: Omit<Props, "open"> & { scope: OfflineQueueScope }) {
  const scope = useMemo(() => scopeInput, [scopeInput.tenantId, scopeInput.userId]);
  const navigate = useNavigate();
  const toast = useToast();
  const [, render] = useState(0);
  const sessionRef = useRef<IntakeDraftSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState("");
  const [recovered, setRecovered] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoCategory, setPhotoCategory] = useState<PhotoCategory>("exterior");
  const checkingPhotos = useRef(false);
  const [preparingPhotos, setPreparingPhotos] = useState(false);
  const [uploaded, setUploaded] = useState({ total: 0, exterior: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [exitChoice, setExitChoice] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const notifiedVehicle = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    let release: (() => void) | undefined;
    const initialize = async () => {
      const stored = await readVehicleIntakeDraft(scope);
      if (!active) return;
      const draft = stored ? intakeDraftSchema.parse(stored) : emptyIntakeDraft();
      const session = new IntakeDraftSession(draft, {
        save: (value) => writeVehicleIntakeDraft(scope, value),
        send: (mutation) => {
          if (!active) throw new Error("Vuelve a abrir el alta para continuar el guardado.");
          return apiFetch<{ id: string }>(mutation.path, {
            method: mutation.method, headers: { "Idempotency-Key": mutation.key },
            body: JSON.stringify(mutation.body), signal: AbortSignal.timeout(15_000),
          });
        },
        onChange: () => { if (active) render((value) => value + 1); },
      });
      sessionRef.current = session;
      setRecovered(Boolean(stored));
      setLoading(false);
      if (draft.vehicleId) {
        // Solo consulta fotos: no pisa los campos pendientes del borrador local.
        void apiFetch<VehicleDetail>(`/vehicles/${draft.vehicleId}`).then((vehicle) => {
          if (active) setUploaded({ total: vehicle.photoCount, exterior: 0 });
        }).catch(() => undefined);
      }
    };
    const start = async () => {
      // StrictMode desmonta el primer efecto inmediatamente. No solicitar su
      // bloqueo: podría impedir que el segundo montaje abra esta misma alta.
      await Promise.resolve();
      if (!active) return;
      if (navigator.locks) {
        await navigator.locks.request(`vehicle-intake:${offlineQueueOwner(scope)}`, { ifAvailable: true }, async (lock) => {
          if (!active) return;
          if (!lock) throw new Error("Hay un alta abierta en otra pestaña. Continúa allí o ciérrala para recuperar aquí el borrador.");
          const held = new Promise<void>((resolve) => { release = resolve; });
          await initialize();
          if (active) await held;
        });
      } else await initialize();
    };
    void start().catch((error) => {
      if (active) { setBootError(error instanceof Error && !storedDataError(error) ? error.message : "No se ha podido recuperar el borrador. No lo hemos borrado; cierra y vuelve a intentarlo."); setLoading(false); }
    });
    return () => { active = false; release?.(); };
  }, [scope]);

  const session = sessionRef.current;
  const draft = session?.draft;
  const fields = draft?.fields;
  const step = draft?.step ?? 0;

  useEffect(() => {
    if (!session || !fields) return;
    const timer = window.setTimeout(() => { void session.flush(); }, 850);
    return () => window.clearTimeout(timer);
  }, [session, fields]);

  useEffect(() => {
    if (!session) return;
    const online = () => { void session.flush(); };
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, [session]);

  useEffect(() => {
    if (draft?.vehicleId && draft.vehicleId !== notifiedVehicle.current) {
      notifiedVehicle.current = draft.vehicleId;
      onCreated?.();
    }
  }, [draft?.vehicleId, onCreated]);

  useEffect(() => {
    const protect = (event: BeforeUnloadEvent) => {
      if (uploading || photoFiles.length || session?.savingLocally || session?.status === "saving" || session?.status === "error") event.preventDefault();
    };
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, [uploading, photoFiles.length, session]);

  const edit = (key: keyof IntakeFields, value: string) => session?.edit({ [key]: value });
  const moveTo = (next: IntakeDraft["step"]) => {
    session?.step(next);
    requestAnimationFrame(() => headingRef.current?.focus());
  };

  const close = async (discardSelection = false) => {
    if (uploading || busy || checkingPhotos.current) return;
    if (photoFiles.length && !discardSelection) { setExitChoice(true); return; }
    setBusy(true);
    try {
      // También vacía el último cambio, aunque aún no haya vencido el debounce.
      await session?.flush();
      await session?.settled();
      if (session?.draft.vehicleId) onCreated?.();
      if (session && session.status !== "saved") toast("Borrador conservado en este dispositivo. Lo recuperarás al volver a Nuevo vehículo.", "info");
      onClose();
    } catch (error) { toast(error instanceof Error ? error.message : "No se ha guardado el borrador.", "error"); }
    finally { setBusy(false); }
  };

  const advance = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || !fields || !intakeHasIdentity(fields)) return;
    setBusy(true);
    try {
      await session.flush(true);
      await session.persisted();
      moveTo(step === 0 ? 1 : 2);
    } catch (error) { toast(error instanceof Error ? error.message : "No se ha guardado.", "error"); }
    finally { setBusy(false); }
  };

  const finish = async (scene = false) => {
    if (!session || photoFiles.length) return;
    setBusy(true);
    try {
      if (!await session.flush()) return;
      const vehicleId = session.draft.vehicleId;
      if (!vehicleId) return;
      await writeVehicleIntakeDraft(scope, null);
      onClose();
      navigate(scene ? vehiclePhotoSceneFlowUrl(vehicleId) : `/app/stock/${vehicleId}`);
    } catch (error) { toast(error instanceof Error ? error.message : "No se ha podido terminar el alta.", "error"); }
    finally { setBusy(false); }
  };

  const selectPhotos = async (files: FileList | null) => {
    if (!files?.length || checkingPhotos.current || uploading) return;
    checkingPhotos.current = true; setPreparingPhotos(true);
    try {
      const next = await prepareVehiclePhotoFiles(photoFiles, [...files]);
      if (next.duplicates) toast(`${next.duplicates} ${next.duplicates === 1 ? "foto repetida omitida" : "fotos repetidas omitidas"}.`, "info");
      if (next.invalid || next.overflow) toast("Usa JPG, PNG o WebP de hasta 25 MB; máximo 40 fotos por lote.", "info");
      setPhotoFiles(next.files);
    } catch { toast("No se han podido comprobar las fotos. Selecciónalas de nuevo.", "error"); }
    finally { checkingPhotos.current = false; setPreparingPhotos(false); }
  };

  const uploadPhotos = async () => {
    if (!draft?.vehicleId || !photoFiles.length || uploading || checkingPhotos.current) return;
    const batch = [...photoFiles];
    const category = photoCategory;
    setUploading(true);
    setUploadProgress({ done: 0, total: batch.length });
    let duplicates = 0;
    try {
      for (const [index, file] of batch.entries()) {
        const result = await uploadVehiclePhoto(draft.vehicleId, file, category);
        if (result.duplicate) duplicates++;
        setPhotoFiles((current) => removeVehiclePhotoFile(current, file));
        if (!result.duplicate) setUploaded((current) => ({ total: current.total + 1, exterior: current.exterior + (category === "exterior" ? 1 : 0) }));
        setUploadProgress({ done: index + 1, total: batch.length });
      }
      toast(duplicates ? `Guardadas ${batch.length - duplicates} fotos; ${duplicates} repetidas no se han vuelto a subir.` : "Fotos guardadas. Puedes añadir otro grupo o ir a la ficha.");
    } catch (error) { toast(error instanceof Error ? error.message : "No se han podido subir todas las fotos.", "error"); }
    finally { setUploading(false); }
  };

  const errors = fields ? intakeFieldErrors(fields) : {};
  const missing = fields ? intakeMissingData(fields) : [];
  const done = [fields ? intakeHasIdentity(fields) : false, missing.length === 0, uploaded.total > 0];
  const titles = ["Identifica el vehículo", "Completa lo que ya sabes", "Añade sus fotografías"];
  const descriptions = ["Matrícula o bastidor. Si aún no los tienes, empieza con marca y modelo.", "Los cambios se guardan al escribir. Lo que falte quedará señalado en la ficha.", "Sube exterior e interior por separado. Podrás añadir más fotos y elegir un escenario después."];
  const disabled = busy || uploading || preparingPhotos;
  const field = (key: keyof IntakeFields, label: string, placeholder: string, maxLength: number, numeric = false) => (
    <label className="field" key={key}><span>{label}</span>
      <input name={key} value={fields?.[key] ?? ""} maxLength={maxLength} placeholder={placeholder} disabled={disabled} autoFocus={key === "registration"}
        inputMode={numeric ? (key === "mileage" ? "numeric" : "decimal") : "text"}
        autoCapitalize={key === "registration" || key === "vin" ? "characters" : undefined}
        aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `intake-${key}-error` : undefined}
        onChange={(event) => edit(key, key === "registration" || key === "vin" ? event.target.value.toUpperCase() : event.target.value)} />
      {errors[key] && <small className="error-copy" id={`intake-${key}-error`}>{errors[key]}</small>}
    </label>
  );

  return <Modal open onClose={() => { void close(); }} title={recovered ? "Continuar alta del vehículo" : "Nuevo vehículo"} size="wide">
    <div className="vehicle-intake">
      {loading ? <p role="status">Recuperando tu borrador…</p> : bootError ? <p role="alert" className="error-copy">{bootError}</p> : session && fields && <>
        <ol className="vehicle-intake__steps" aria-label="Pasos del alta">
          {["Identificación", "Datos", "Fotos"].map((label, index) => <li key={label}>
            <button type="button" aria-current={step === index ? "step" : undefined} disabled={disabled || (index > 0 && !intakeHasIdentity(fields))}
              onClick={() => {
                if (index > step && !draft?.vehicleId && !draft?.creationRequested) {
                  // Los pasos son navegables, pero avanzar también confirma el alta.
                  void session.flush(true);
                }
                moveTo(index as IntakeDraft["step"]);
              }}>
              <span className="vehicle-intake__step-number" aria-hidden="true">{done[index] && step !== index ? <Check /> : `0${index + 1}`}</span>
              <span><strong>{label}</strong><small>{index === 0 ? done[0] ? "Identificado" : "Por identificar" : index === 1 ? done[1] ? "Datos completos" : "Puedes completar después" : uploaded.total ? `${uploaded.total} guardadas` : "Sin fotos todavía"}</small></span>
            </button>
          </li>)}
        </ol>
        <div className={`vehicle-intake__save vehicle-intake__save--${session.status}`} role="status" aria-live="polite">
          {session.status === "saved" ? <CheckCircle2 /> : session.status === "saving" ? <RefreshCw /> : <Laptop />}
          <span>{session.status === "error" ? session.error : session.savingLocally ? "Guardando borrador…" : session.status === "saved" ? "Cambios guardados en la ficha" : session.status === "saving" ? "Guardando en la ficha…" : draft?.vehicleId ? "Guardando cambios…" : "Borrador en este dispositivo · se crea la ficha al continuar"}</span>
          {session.status === "error" && <button type="button" className="text-button" disabled={disabled} onClick={() => { void session.flush(); }}>Reintentar</button>}
        </div>
        {recovered && <p className="vehicle-intake__resumed"><CheckCircle2 />Hemos recuperado tu alta. Continúa donde lo dejaste.</p>}
        <header className="vehicle-intake__heading"><p className="eyebrow">Paso {step + 1} de 3</p><h3 ref={headingRef} tabIndex={-1}>{titles[step]}</h3><p>{descriptions[step]}</p></header>
        {step < 2 ? <form className="form-stack" onSubmit={(event) => { void advance(event); }}>
          {step === 0 ? <>
            <div className="field-row">{field("registration", "Matrícula", "1234 ABC", 16)}{field("vin", "Bastidor (VIN)", "Si aún no hay matrícula", 32)}</div>
            <div className="field-row">{field("make", "Marca", "SEAT", 80)}{field("model", "Modelo", "León", 120)}</div>
            <p className="form-helper">Solo necesitas una forma de identificarlo. No hace falta rellenarlo todo ahora.</p>
          </> : <>
            <div className="field-row">{field("mileage", "Kilómetros", "Ej. 46200", 20, true)}{field("targetPrice", "Precio de venta (€)", "Ej. 15900", 20, true)}</div>
            <details className="vehicle-intake__optional"><summary>Versión y acabado <span>Opcional</span></summary>{field("version", "Versión", "Ej. FR 1.5 TSI 150 CV", 160)}</details>
            <div className="vehicle-intake__pending"><strong>{missing.length ? "Podrás completar después" : "Datos básicos completos"}</strong><p>{missing.length ? missing.join(" · ") : "El siguiente paso son las fotos. La compra se registra desde la ficha, sin volver a introducir estos datos."}</p></div>
          </>}
          <footer className="vehicle-intake__actions"><button type="button" className="button button--ghost" disabled={disabled} onClick={() => { void close(); }}>Continuar después</button>
            <div>{step > 0 && <button type="button" className="button button--secondary" disabled={disabled} onClick={() => moveTo(0)}><ArrowLeft />Atrás</button>}
              <button type="submit" className="button button--primary" disabled={disabled || !intakeHasIdentity(fields) || Object.keys(errors).length > 0}>{busy ? "Guardando…" : step === 0 ? "Guardar y continuar" : "Continuar a fotos"}<ArrowRight /></button></div>
          </footer>
        </form> : <section className="form-stack quick-photo-intake">
          {!draft?.vehicleId ? <div className="vehicle-intake__pending"><strong>Guarda primero la ficha para subir fotos</strong><p>Los datos siguen en este dispositivo. Con conexión, pulsa Guardar ficha para continuar.</p><button type="button" className="button button--primary" disabled={disabled} onClick={() => { void session.flush(true); }}>Guardar ficha</button></div> : <>
            <label className="field"><span>Tipo de fotos</span><select value={photoCategory} disabled={disabled} onChange={(event) => setPhotoCategory(event.target.value as PhotoCategory)}><option value="exterior">Exterior</option><option value="interior">Interior</option><option value="engine">Motor</option><option value="defect">Detalles y desperfectos</option></select></label>
            <button type="button" className="file-drop quick-photo-intake__drop" disabled={disabled} onClick={() => galleryRef.current?.click()}><Camera /><strong>{preparingPhotos ? "Comprobando fotos…" : photoFiles.length ? `${photoFiles.length} fotos listas para subir` : "Elige tus fotos"}</strong><small>JPG, PNG o WebP · hasta 25 MB por foto · sin duplicados</small></button>
            <input ref={galleryRef} hidden multiple type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { selectPhotos(event.target.files); event.target.value = ""; }} />
            <input ref={cameraRef} hidden type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event) => { selectPhotos(event.target.files); event.target.value = ""; }} />
            <div className="quick-photo-intake__tools"><button type="button" className="button button--secondary" disabled={disabled} onClick={() => cameraRef.current?.click()}><Camera />Usar cámara</button>{photoFiles.length > 0 && <button type="button" className="button button--ghost" disabled={disabled} onClick={() => setPhotoFiles([])}>Quitar selección</button>}</div>
            {photoFiles.length > 0 && <ul className="quick-photo-intake__files">{photoFiles.map((file, index) => <li key={`${index}:${file.name}`}><span>{file.name}</span><button type="button" className="icon-button" aria-label={`Quitar ${file.name}`} disabled={disabled} onClick={() => setPhotoFiles((current) => removeVehiclePhotoFile(current, file))}><X /></button></li>)}</ul>}
            {uploaded.total > 0 && <div className="vehicle-intake__photo-count"><CheckCircle2 /><span>{uploaded.total} {uploaded.total === 1 ? "foto guardada" : "fotos guardadas"}. Puedes añadir otro grupo.</span></div>}
            {uploading && <div className="quick-photo-intake__progress" role="status"><span style={{ width: `${uploadProgress.done / uploadProgress.total * 100}%` }} /><small>Subiendo {uploadProgress.done} de {uploadProgress.total}</small></div>}
          </>}
          <div className="vehicle-intake__pending"><strong>Después del alta</strong><p>En la ficha verás qué falta para ponerlo a la venta: registrar la compra, confirmar la recepción y completar los datos y fotos necesarios. No se publicará nada automáticamente.</p></div>
          <footer className="vehicle-intake__actions"><button type="button" className="button button--secondary" disabled={disabled} onClick={() => moveTo(1)}><ArrowLeft />Atrás</button><div>
            {photoFiles.length ? <button type="button" className="button button--primary" disabled={disabled} onClick={() => { void uploadPhotos(); }}>{uploading ? `Subiendo ${uploadProgress.done}/${uploadProgress.total}` : `Subir ${photoFiles.length} fotos`}<ArrowRight /></button> : <>
              {uploaded.exterior > 0 && <button type="button" className="button button--secondary" disabled={disabled} onClick={() => { void finish(true); }}><Sparkles />Elegir escenario</button>}
              <button type="button" className="button button--primary" disabled={disabled || !draft?.vehicleId} onClick={() => { void finish(); }}>{busy ? "Guardando…" : "Ir a la ficha"}<ArrowRight /></button>
            </>}
          </div></footer>
          <button type="button" className="text-button" disabled={disabled} onClick={() => { void close(); }}>Continuar después</button>
        </section>}
        {exitChoice && <section className="vehicle-intake__pending" role="alert"><strong>Hay {photoFiles.length} fotos seleccionadas sin subir</strong><p>Los datos se conservarán, pero tendrás que volver a elegir estas fotos si cierras ahora.</p><div className="vehicle-intake__exit"><button type="button" className="button button--primary" onClick={() => { setExitChoice(false); moveTo(2); }}>Seguir con las fotos</button><button type="button" className="button button--ghost" onClick={() => { void close(true); }}>Cerrar y conservar datos</button></div></section>}
      </>}
    </div>
  </Modal>;
}

function storedDataError(error: Error) { return error.name === "ZodError"; }
